import type { ReactNode } from "react";
import { dateUtil } from "@sdeapps/react-core";
import ToastMessages from "constants/ToastMessages";
import type { MandatSdeaCreateModel } from "models";
import { enqueueSnackbar } from "notistack";
import { FormProvider, useForm } from "react-hook-form";
import type { SubmitHandler } from "react-hook-form";
import { mandatsService } from "services";
import { useSnackbarErrorHandler, withComponentErrorBoundary } from "utils/errorHandling";
import oracleFonctions from "constants/fonctions_oracle.json";
import type { MandatSdeaFormModel } from "../../models/Mandat";
import MandatSdeaTableRowFormContent from "./MandatSdeaTableRowFormContent";

interface NewMandatSdeaTableRowProps {
  idElu: string;
  cancel?: () => void;
  refetchElu?: () => void;
}

function toCreateData(data: MandatSdeaFormModel, idElu: string): MandatSdeaCreateModel {
  const createData: MandatSdeaCreateModel = {
    idPersonne: idElu,
    dateDebut: dateUtil.format(data.dateDebutMandat, "yyyy-MM-dd"),
    idFonction: data.fonction.id,
    libelleFonction: data.fonction.libelle,
    typeMandat: oracleFonctions.find((f) => f.ID === data.fonction.id)?.TYPE ?? "SDEA",
    competence: data.competence,
    idTerritoire: data.territoire?.id,
    libelleTerritoire: data.territoire?.libelle,
    idCommune: data.commune?.id,
    libelleCommune: data.commune?.libelle,
    idPerimetre: data.perimetre?.id,
    libellePerimetre: data.perimetre?.libelle,
  };

  return createData;
}

function NewMandatSdeaTableRow({
  idElu,
  cancel,
  refetchElu,
}: Readonly<NewMandatSdeaTableRowProps>): ReactNode {
  const formMethods = useForm<MandatSdeaFormModel>({
    shouldFocusError: false,
    shouldUnregister: true,
  });
  const { handleSubmit } = formMethods;

  const { catchErrors: catchCreateErrors, isLoading: isCreating } = useSnackbarErrorHandler();

  const submitMandat: SubmitHandler<MandatSdeaFormModel> = async function (
    data: MandatSdeaFormModel
  ): Promise<void> {
    async function createMandat(): Promise<void> {
      const createData = toCreateData(data, idElu);
      await mandatsService.create(createData);

      enqueueSnackbar({
        variant: "success",
        message: ToastMessages.CREATED_MANDAT,
      });

      refetchElu?.();
    }

    await catchCreateErrors(createMandat);
  };

  return (
    <FormProvider {...formMethods}>
      <MandatSdeaTableRowFormContent
        sendData={handleSubmit(submitMandat)}
        cancel={cancel}
        isSending={isCreating}
        variant="create"
      />
    </FormProvider>
  );
}

const NewMandatSdeaTableRowWithErrorBoundary = withComponentErrorBoundary(NewMandatSdeaTableRow);
export default NewMandatSdeaTableRowWithErrorBoundary;
